import * as LabelPrimitive from "@radix-ui/react-label";
import { useId, useState } from "react";
import { cn } from "./cn.ts";
import { formatMinor, type NumberSeparators, parseMoney } from "./money.ts";

export type FieldKind = "text" | "number" | "money" | "date" | "bool" | "select" | "relation";
export type FieldValue = string | boolean | null;

export interface FieldInputSpec {
  readonly id: string;
  readonly label: string;
  readonly type: FieldKind;
  readonly required: boolean;
  /** select: option ACTIVE (đã dịch nhãn); relation: bản ghi chọn được. */
  readonly options?: readonly { readonly value: string; readonly label: string }[];
  readonly currency?: string;
  readonly minorUnitScale?: number;
}

export interface FieldInputLabels {
  readonly yes: string;
  readonly no: string;
  readonly none: string;
  readonly invalidMoney: string;
}

const inputCls = "h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-3 text-sm text-(--saa-fg) outline-none focus:border-(--saa-ring) focus:ring-2 focus:ring-(--saa-ring)/30";

/**
 * Ô nhập theo kiểu field (ADR-002). Giá trị ra đúng mã hoá của API: number → chuỗi thập phân, money → chuỗi minor unit theo
 * `minorUnitScale` (không số thực), date → YYYY-MM-DD, select / relation → id, bool → true / false, rỗng → null.
 */
export function FieldInput({ field, value, onChange, error, labels, separators }: { field: FieldInputSpec; value: FieldValue; onChange: (v: FieldValue) => void; error?: string | undefined; labels: FieldInputLabels; separators: NumberSeparators }) {
  const id = useId();
  const scale = field.minorUnitScale ?? 0;
  const [moneyText, setMoneyText] = useState(() => (field.type === "money" && typeof value === "string" ? formatMinor(value, scale, separators) : ""));
  const [moneyBad, setMoneyBad] = useState(false);
  const err = error ?? (moneyBad ? labels.invalidMoney : undefined);
  const describedBy = err ? `${id}-error` : undefined;
  const common = { id, "aria-invalid": err ? true : undefined, "aria-describedby": describedBy, required: field.required } as const;
  let control;
  switch (field.type) {
    case "bool":
      control = (
        <select {...common} className={inputCls} value={value === null ? "" : String(value)} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}>
          <option value="">{labels.none}</option>
          <option value="true">{labels.yes}</option>
          <option value="false">{labels.no}</option>
        </select>
      );
      break;
    case "select":
    case "relation":
      control = (
        <select {...common} className={inputCls} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{labels.none}</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    case "money":
      control = (
        <div className="flex items-center gap-2">
          <input
            {...common}
            inputMode="decimal"
            className={cn(inputCls, "flex-1", err && "border-(--saa-danger)")}
            value={moneyText}
            onChange={(e) => {
              setMoneyText(e.target.value);
              const minor = e.target.value.trim() ? parseMoney(e.target.value, scale, separators) : null;
              setMoneyBad(e.target.value.trim() !== "" && minor === null);
              if (e.target.value.trim() === "" || minor !== null) onChange(minor);
            }}
          />
          <span className="text-xs text-(--saa-fg-muted)">{field.currency}</span>
        </div>
      );
      break;
    default:
      control = (
        <input
          {...common}
          type={field.type === "date" ? "date" : "text"}
          inputMode={field.type === "number" ? "decimal" : undefined}
          className={cn(inputCls, err && "border-(--saa-danger)")}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        />
      );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={id} className="text-sm font-medium text-(--saa-fg)">
        {field.label}
        {field.required ? <span aria-hidden> *</span> : null}
      </LabelPrimitive.Root>
      {control}
      {err ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-(--saa-danger)">
          {err}
        </p>
      ) : null}
    </div>
  );
}

/** Form bản ghi sinh từ field ACTIVE (RecordForm). */
export function RecordForm({ fields, values, onChange, errors, labels, separators }: { fields: readonly FieldInputSpec[]; values: Readonly<Record<string, FieldValue>>; onChange: (fieldId: string, v: FieldValue) => void; errors?: Readonly<Record<string, string>>; labels: FieldInputLabels; separators: NumberSeparators }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((f) => (
        <FieldInput key={f.id} field={f} value={values[f.id] ?? null} onChange={(v) => onChange(f.id, v)} error={errors?.[f.id]} labels={labels} separators={separators} />
      ))}
    </div>
  );
}

/** Chi tiết bản ghi: cặp nhãn / giá trị đã định dạng (RecordDetail). */
export function RecordDetail({ title, items }: { title: string; items: readonly { readonly id: string; readonly label: string; readonly value: React.ReactNode }[] }) {
  return (
    <section aria-label={title}>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        {items.map((i) => (
          <div key={i.id} className="contents">
            <dt className="text-(--saa-fg-muted)">{i.label}</dt>
            <dd className="break-words">{i.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
